/**
 * analytics.js — PostHog product analytics for the backoffice.
 *
 * One shared client. The API key + host come from Vite env vars
 * (VITE_POSTHOG_KEY / VITE_POSTHOG_HOST), never hardcoded. Every event carries
 * `app: 'backoffice'` so the same PostHog project can distinguish backoffice
 * events from field-recorder events.
 *
 * Safe no-op when no key is configured, so the app runs fine without analytics.
 */
import posthog from 'posthog-js';

// Public PostHog project key — safe to ship in the client bundle. Falls back to
// the known project so analytics works without a build-time env var; overridable
// via VITE_POSTHOG_KEY. One project for all environments; events carry `app` +
// `environment` super-properties to distinguish them.
const KEY = import.meta.env.VITE_POSTHOG_KEY || 'phc_ngC9QPD67f6BdQPJFCDzHisYofVYFvPfwHDroJJ472Ax';
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

let enabled = false;

/**
 * Derive the deployment environment from the hostname — zero-config across
 * every Cloudflare preview branch. Production runs on a custom domain.
 */
function detectEnvironment() {
  const h = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local')) {
    return 'development';
  }
  if (h.endsWith('.pages.dev')) return 'staging';
  return 'production';
}

/** Initialise once on app startup. No-op when no key is configured. */
export function initAnalytics() {
  if (enabled) return;
  if (!KEY) {
    console.info('[analytics] VITE_POSTHOG_KEY not set — PostHog disabled');
    return;
  }
  posthog.init(KEY, {
    api_host: HOST,
    capture_pageview: true,
    person_profiles: 'identified_only',
  });
  // super properties on every event — filter by these in PostHog
  posthog.register({ app: 'backoffice', environment: detectEnvironment() });
  enabled = true;
}

/** Tie subsequent events to a logged-in user. */
export function identifyUser(user) {
  if (!enabled || !user) return;
  const id = user.id ?? user.email;
  if (!id) return;
  posthog.identify(String(id), {
    email: user.email,
    role: user.role,
    company_id: user.company_id,
    company_name: user.company_name,
  });
  // Associate events with the organisation. Group analytics handles a person
  // belonging to multiple orgs: each event attributes to the active org. Also
  // filterable as person properties (company_id/company_name) above.
  if (user.company_id) {
    posthog.group('organization', String(user.company_id), {
      name: user.company_name || String(user.company_id),
    });
  }
}

/** Capture a product event. Silently ignored when analytics is disabled. */
export function capture(event, properties) {
  if (!enabled) return;
  posthog.capture(event, properties);
}

/** Clear the identified user (e.g. on logout). */
export function resetAnalytics() {
  if (!enabled) return;
  posthog.reset();
}
